/* RESPONSIBLE TEAM: team-inbox */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type Store from '@ember-data/store';
import { ajaxDelete, post } from 'embercom/lib/ajax';

interface Translation {
  [key: string]: string;
}

interface GlossaryItem {
  id: number;
  translations: Translation;
  non_translatable: boolean;
  created_at: string;
  updated_at: string;
}

interface ColumnDefinition {
  label: string;
  valuePath: string;
  width?: string;
}

let PAGE_SIZE = 100;

export default class GlossaryService extends Service {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare intl: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: $TSFixMe;
  @service declare languagesService: $TSFixMe;

  @tracked glossary: Array<GlossaryItem> = [];
  @tracked displayGlossary: Array<GlossaryItem> = [];
  @tracked availableLanguages: Set<string> = new Set();
  @tracked currentPage = 1;
  @tracked exists = false;
  @tracked columns: Array<ColumnDefinition> = [];

  get hasLoadedGlossary() {
    return taskFor(this.loadGlossaryItems).performCount > 0;
  }

  async loadGlossary() {
    return taskFor(this.loadGlossaryItems).perform();
  }

  deleteGlossary(rowDataId: number | undefined) {
    if (!rowDataId) {
      return;
    }

    return taskFor(this.deleteGlossaryItem).perform(rowDataId);
  }

  deleteGlossaries() {
    return taskFor(this.deleteAllGlossaries).perform();
  }

  @task({ restartable: true }) *loadGlossaryItems(): TaskGenerator<void> {
    let languageSettings = yield this.store.findRecord(
      'messenger-settings/languages',
      this.appService.app.id,
    );
    let nonDefaultPermittedLocales = languageSettings.supportedLocales
      .filter((locale: any) => locale.isPermitted)
      .map((locale: any) => locale.localeId);

    let response = yield fetch(`/ember/multilingual_glossary?app_id=${this.appService.app.id}`);
    let data = yield response.json();
    this.glossary = data;
    this.exists = data.length > 0;
    this.loadInitialPage();

    this.availableLanguages = new Set();
    for (let item of this.glossary) {
      if (item.translations) {
        Object.keys(item.translations).forEach((langCode) => {
          // Only add language if it's in permitted locales or is the default locale
          if (
            nonDefaultPermittedLocales.includes(langCode) ||
            langCode === languageSettings.defaultLocale
          ) {
            this.availableLanguages.add(langCode);
          }
        });
      }
    }

    this.buildColumns();
  }

  loadInitialPage() {
    this.currentPage = 1;
    this.displayGlossary = this.glossary.slice(0, PAGE_SIZE);
  }

  loadMoreItems() {
    let nextPage = this.currentPage + 1;
    let startIndex = this.currentPage * PAGE_SIZE;
    let endIndex = nextPage * PAGE_SIZE;

    let moreItems = this.glossary.slice(startIndex, endIndex);
    this.displayGlossary = [...this.displayGlossary, ...moreItems];
    this.currentPage = nextPage;
  }

  @task({ restartable: true }) *importGlossary(
    csvData: string[][],
    context = 'from_settings',
  ): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      yield post('/ember/multilingual_glossary/import', {
        app_id: appId,
        csv_glossary_upload: csvData,
      });

      this.exists = true;
      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.workspace.multilingual.glossary.import.success'),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'uploaded',
        object: 'multilingual_glossary',
        context,
      });

      yield taskFor(this.loadGlossaryItems).perform();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.multilingual.glossary.import.error'),
      );
      throw error;
    }
  }

  @task({ restartable: true }) *deleteAllGlossaries(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      yield ajaxDelete('/ember/multilingual_glossary/delete', { app_id: appId });

      this.exists = false;
      yield taskFor(this.loadGlossaryItems).perform();

      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.workspace.multilingual.glossary.delete-all.success'),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'deleted',
        object: 'multilingual_glossary',
        context: 'from_settings',
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.multilingual.glossary.delete-all.error'),
      );
      throw error;
    }
  }

  @task({ restartable: true }) *deleteGlossaryItem(rowDataId: number): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      yield ajaxDelete(`/ember/multilingual_glossary/${rowDataId}`, { app_id: appId });

      this.loadInitialPage();
      yield taskFor(this.loadGlossaryItems).perform();

      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.workspace.multilingual.glossary.notification.delete-success'),
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.multilingual.glossary.delete-all.error'),
      );
    }
  }

  async exportGlossary() {
    try {
      let appId = this.appService.app.id;
      let queryParams = new URLSearchParams({ app_id: appId }).toString();
      let endpoint = `/ember/multilingual_glossary/export?${queryParams}`;

      // Trigger file download by creating a temporary link
      let link = document.createElement('a');
      link.setAttribute('href', endpoint);
      link.setAttribute('download', 'glossary_export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'exported',
        object: 'multilingual_glossary',
        context: 'from_settings',
      });
    } catch (error) {
      let errorMessage = this.intl.t('new-settings.workspace.multilingual.glossary.export.error');

      // Extract error message from the API response if available
      if (error?.jqXHR?.responseJSON?.error) {
        errorMessage = error.jqXHR.responseJSON.error;
      }

      this.notificationsService.notifyError(errorMessage);
      throw error;
    }
  }

  buildColumns() {
    // Prefer workspace default locale; fallback to teammate UI locale
    let defaultFromWorkspace = this.languagesService?.languages?.defaultLocale;
    let appLocale = (defaultFromWorkspace || this.session.locale).split('-')[0];
    let languages = this.sortedLanguages;

    if (languages.includes(appLocale)) {
      let appLocaleIndex = languages.indexOf(appLocale);
      languages.splice(appLocaleIndex, 1);
      languages.unshift(appLocale);
    }

    this.columns = languages.map((langCode) => ({
      label:
        langCode === appLocale
          ? this.intl.t('new-settings.workspace.multilingual.glossary.original-language')
          : this.getLanguageDisplayName(langCode),
      valuePath: langCode === 'id' ? 'id_lang' : langCode,
      width: `${Math.floor(100 / languages.length)}%`,
    }));
  }

  getLanguageDisplayName(langCode: string): string {
    return this.getLanguageFromCode(langCode, this.session.locale);
  }

  getLanguageFromCode(languageCode: string, locale: string): string {
    try {
      let baseLanguageCode = languageCode.split('-')[0];

      let languageNames = new Intl.DisplayNames([locale], { type: 'language' });

      return languageNames.of(languageCode) || languageNames.of(baseLanguageCode) || languageCode;
    } catch (e) {
      return languageCode;
    }
  }

  get canLoadMore() {
    return this.displayGlossary.length < this.glossary.length;
  }

  get isLoading() {
    return taskFor(this.loadGlossaryItems).isRunning;
  }

  get totalItems() {
    return this.glossary.length;
  }

  get sortedLanguages() {
    return Array.from(this.availableLanguages).sort((a, b) => {
      let displayNameA = this.getLanguageDisplayName(a);
      let displayNameB = this.getLanguageDisplayName(b);
      return displayNameA.localeCompare(displayNameB);
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    glossaryService: GlossaryService;
  }
}
